import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Chip,
  Tooltip,
  Button,
  Alert,
  Collapse
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as DuplicateIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '8px',
  maxHeight: '600px',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: '#2A2A2A',
  '& .MuiTableCell-head': {
    color: '#fff',
    fontWeight: 'bold',
    borderBottom: '1px solid #333',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, hasError }) => ({
  backgroundColor: hasError ? 'rgba(244, 67, 54, 0.05)' : 'transparent',
  '&:hover': {
    backgroundColor: hasError ? 'rgba(244, 67, 54, 0.1)' : 'rgba(255, 255, 255, 0.05)',
  },
  '& .MuiTableCell-root': {
    borderBottom: '1px solid #333',
    color: '#ccc',
  },
}));

const AnswerPreview = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
}));

const AnswerChip = styled(Chip)(({ theme, isCorrect }) => ({
  fontSize: '0.75rem',
  height: '20px',
  backgroundColor: isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.1)',
  color: isCorrect ? '#4CAF50' : '#ccc',
  border: `1px solid ${isCorrect ? '#4CAF50' : '#555'}`,
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  color: '#ccc',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
}));

const ReviewTable = ({ 
  questions, 
  validationResults, 
  onEditQuestion, 
  onDeleteQuestion, 
  onViewQuestion,
  onOpenDuplicateManager,
  lookupData,
  songData 
}) => {
  const [expandedAnswers, setExpandedAnswers] = useState(new Set());

  // Toggle answer expansion
  const toggleAnswerExpansion = (questionIndex) => {
    const newExpanded = new Set(expandedAnswers);
    if (newExpanded.has(questionIndex)) {
      newExpanded.delete(questionIndex);
    } else {
      newExpanded.add(questionIndex);
    }
    setExpandedAnswers(newExpanded);
  };

  // Calculate statistics
  const validQuestions = validationResults.filter(result => result.isValid).length;
  const invalidQuestions = validationResults.filter(result => !result.isValid).length;
  const totalQuestions = questions.length;

  // Render answer preview
  const renderAnswerPreview = (answers, questionIndex, isExpanded) => {
    if (!answers || answers.length === 0) {
      return <Typography variant="body2" sx={{ color: '#f44336' }}>No answers</Typography>;
    }

    const correctCount = answers.filter(a => a.correct).length;
    
    if (!isExpanded) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            {answers.length} answers ({correctCount} correct)
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => toggleAnswerExpansion(questionIndex)}
            sx={{ color: '#4CAF50' }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            {answers.length} answers ({correctCount} correct)
          </Typography>
          <IconButton 
            size="small" 
            onClick={() => toggleAnswerExpansion(questionIndex)}
            sx={{ color: '#4CAF50' }}
          >
            <ExpandLessIcon fontSize="small" />
          </IconButton>
        </Box>
        <AnswerPreview>
          {answers.map((answer, index) => (
            <AnswerChip
              key={index}
              label={`${String.fromCharCode(65 + index)}: ${answer.text.length > 30 ? answer.text.substring(0, 30) + '...' : answer.text}`}
              size="small"
              isCorrect={answer.correct}
            />
          ))}
        </AnswerPreview>
      </Box>
    );
  };

  // Render validation status
  const renderValidationStatus = (validationResult) => {
    if (validationResult.isValid) {
      return (
        <Tooltip title="Question is valid">
          <CheckIcon sx={{ color: '#4CAF50', fontSize: 20 }} />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={validationResult.errors.join(', ')}>
        <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />
      </Tooltip>
    );
  };

  if (questions.length === 0) {
    return (
      <Alert severity="info" sx={{ backgroundColor: 'rgba(33, 150, 243, 0.1)' }}>
        No questions to review. Please upload a CSV file first.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary and Actions */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#fff' }}>
            Review Questions ({totalQuestions})
          </Typography>
          <Button
            startIcon={<DuplicateIcon />}
            onClick={onOpenDuplicateManager}
            sx={{ color: '#4CAF50' }}
          >
            Manage Duplicates
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <Chip 
            label={`${validQuestions} Valid`}
            sx={{ backgroundColor: '#4CAF50', color: '#fff' }}
          />
          <Chip 
            label={`${invalidQuestions} Invalid`}
            sx={{ backgroundColor: '#f44336', color: '#fff' }}
          />
        </Box>

        {invalidQuestions > 0 && (
          <Alert severity="error" sx={{ backgroundColor: 'rgba(244, 67, 54, 0.1)', mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              ⚠️ Import Blocked: {invalidQuestions} questions have validation errors
            </Typography>
            <Typography variant="body2">
              All validation errors must be fixed before you can import questions.
              Use the Edit button to fix individual questions, or review the error details below.
            </Typography>
          </Alert>
        )}

        {invalidQuestions > 0 && (
          <Box sx={{ mb: 2, p: 2, backgroundColor: 'rgba(244, 67, 54, 0.05)', borderRadius: '8px', border: '1px solid rgba(244, 67, 54, 0.2)' }}>
            <Typography variant="subtitle2" sx={{ color: '#f44336', mb: 1 }}>
              Common Issues Found:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {(() => {
                const errorCounts = {};
                validationResults.forEach(result => {
                  if (!result.isValid) {
                    result.errors.forEach(error => {
                      errorCounts[error] = (errorCounts[error] || 0) + 1;
                    });
                  }
                });

                return Object.entries(errorCounts)
                  .sort(([,a], [,b]) => b - a)
                  .slice(0, 5)
                  .map(([error, count]) => (
                    <Typography key={error} variant="body2" sx={{ color: '#f44336' }}>
                      • {error} ({count} question{count > 1 ? 's' : ''})
                    </Typography>
                  ));
              })()}
            </Box>
          </Box>
        )}
      </Box>

      {/* Questions Table */}
      <StyledTableContainer component={Paper}>
        <Table stickyHeader>
          <StyledTableHead>
            <TableRow>
              <TableCell width="50px">Row</TableCell>
              <TableCell width="60px">Status</TableCell>
              <TableCell width="300px">Question Text</TableCell>
              <TableCell width="80px">Time</TableCell>
              <TableCell width="200px">Answers</TableCell>
              <TableCell width="150px">Clue</TableCell>
              <TableCell width="120px">Actions</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {questions.map((question, index) => {
              const validationResult = validationResults[index] || { isValid: true, errors: [] };
              const hasError = !validationResult.isValid;
              const isExpanded = expandedAnswers.has(index);

              return (
                <StyledTableRow key={index} hasError={hasError}>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
                      {question.rowNumber}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    {renderValidationStatus(validationResult)}
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: hasError ? '#f44336' : '#fff',
                          lineHeight: 1.4,
                          mb: hasError ? 1 : 0
                        }}
                      >
                        {question.questionText.length > 100 
                          ? `${question.questionText.substring(0, 100)}...`
                          : question.questionText
                        }
                      </Typography>
                      {hasError && (
                        <Collapse in={true}>
                          <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(244, 67, 54, 0.1)', borderRadius: '4px' }}>
                            <Typography variant="caption" sx={{ color: '#f44336', fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                              Validation Errors:
                            </Typography>
                            {validationResult.errors.map((error, errorIndex) => (
                              <Typography
                                key={errorIndex}
                                variant="caption"
                                sx={{ color: '#f44336', display: 'block', ml: 1 }}
                              >
                                • {error}
                              </Typography>
                            ))}
                            <Typography variant="caption" sx={{ color: '#ff9800', display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                              Click the Edit button to fix these issues
                            </Typography>
                          </Box>
                        </Collapse>
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {question.timeLimit || 30}s
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    {renderAnswerPreview(question.answers, index, isExpanded)}
                  </TableCell>
                  
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#ccc',
                        fontStyle: question.questionClue ? 'normal' : 'italic'
                      }}
                    >
                      {question.questionClue 
                        ? (question.questionClue.length > 50 
                            ? `${question.questionClue.substring(0, 50)}...`
                            : question.questionClue
                          )
                        : 'No clue'
                      }
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <ActionButton 
                          size="small"
                          onClick={() => onViewQuestion(question, index)}
                          sx={{ color: '#2196F3' }}
                        >
                          <ViewIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                      
                      <Tooltip title="Edit Question">
                        <ActionButton 
                          size="small"
                          onClick={() => onEditQuestion(question, index)}
                          sx={{ color: '#4CAF50' }}
                        >
                          <EditIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                      
                      <Tooltip title="Delete Question">
                        <ActionButton 
                          size="small"
                          onClick={() => onDeleteQuestion(index)}
                          sx={{ color: '#f44336' }}
                        >
                          <DeleteIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  );
};

export default ReviewTable;
